import { useCallback, useEffect, useRef, useState } from 'react';

type AcceptFiles = (files: File[]) => void;

export interface UseFileDropPasteOptions {
  onFiles: AcceptFiles;
  // When true, paste anywhere in the document; otherwise paste only when
  // focus is inside the drop zone (or its descendants).
  pasteOnDocument?: boolean;
  // Disables both drop and paste while uploading / read-only.
  disabled?: boolean;
}

// Centralizes drag-and-drop + Ctrl/Cmd+V file handling so attachment
// dropzones across Task pages stay consistent. Returns a ref to attach to
// the drop zone, the current drag-over state for styling, and a paste
// helper if a host wants to wire it manually.
export function useFileDropPaste({
  onFiles,
  pasteOnDocument = false,
  disabled = false,
}: UseFileDropPasteOptions) {
  const dropRef = useRef<HTMLDivElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Counter avoids flicker when dragging across child elements (each child
  // fires dragenter/dragleave; we only flip styling on the outermost edge).
  const dragDepth = useRef(0);

  const collectFiles = useCallback((list?: FileList | null): File[] => {
    if (!list || list.length === 0) return [];
    return Array.from(list).filter((f) => f && f.size > 0);
  }, []);

  useEffect(() => {
    const el = dropRef.current;
    if (!el || disabled) return;

    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragOver(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragOver(false);
      const files = collectFiles(e.dataTransfer?.files);
      if (files.length) onFiles(files);
    };

    el.addEventListener('dragenter', onDragEnter);
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragenter', onDragEnter);
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [onFiles, collectFiles, disabled]);

  useEffect(() => {
    if (disabled) return;

    const onPaste = (e: ClipboardEvent) => {
      // When focus is inside a text input/textarea/contenteditable, ignore
      // paste so we don't hijack normal text pasting.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
          // Allow paste-to-upload only when the clipboard has files and the
          // user is clearly trying to attach (no text payload).
          const items = e.clipboardData?.items;
          let hasText = false;
          if (items) {
            for (let i = 0; i < items.length; i++) {
              if (items[i].kind === 'string') hasText = true;
            }
          }
          if (hasText) return;
        }
      }
      // Scope check for pasteOnDocument=false: only react when the drop
      // zone contains the active element, or the paste event target is
      // inside the drop zone.
      if (!pasteOnDocument) {
        const el = dropRef.current;
        if (!el) return;
        const active = document.activeElement;
        const insideByTarget = target && el.contains(target);
        const insideByActive = active && el.contains(active);
        if (!insideByTarget && !insideByActive) return;
      }
      const files: File[] = [];
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          if (it.kind === 'file') {
            const f = it.getAsFile();
            if (f && f.size > 0) files.push(f);
          }
        }
      }
      const direct = collectFiles(e.clipboardData?.files);
      const merged = files.length ? files : direct;
      if (merged.length) {
        e.preventDefault();
        onFiles(merged);
      }
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [onFiles, collectFiles, pasteOnDocument, disabled]);

  return { dropRef, isDragOver };
}

export default useFileDropPaste;
